import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity } from '@adornis/baseql/decorators/entity.js';
import { Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { UserListSelectionInterface } from '../../users/list/entities/UserListSelectionInterface.js';

@Entity()
export class SepaExportSettings extends AdornisEntity {
  static override _class = 'SepaExportSettings';

  constructor(doc: PartialEntityData<SepaExportSettings>) {
    super(doc);
  }

  /**
   * The one group whose bookings are exported (export from the group details). Without it the
   * export runs over every group matching {@link billingPositionName}, {@link schoolID} and
   * {@link groupIDs} (export from the person list).
   */
  @Field(() => ID)
  groupID: Maybe<string>;

  /** Person-list export: only groups whose Abrechnungsposition has this name. Unset means all. */
  @Field(() => String)
  billingPositionName: Maybe<string>;

  /** Person-list export: only groups of this school. Unset means all schools. */
  @Field(() => String)
  schoolID: Maybe<string>;

  /** Person-list export: exactly these groups. Unset means all groups matching the filters above. */
  @Field(() => [String])
  groupIDs: Maybe<string[]>;

  /** Person-list export: only bookings of the persons in this selection. */
  @Field(() => UserListSelectionInterface)
  userSelectionInterface: Maybe<UserListSelectionInterface>;

  /**
   * The Stichtag of the export: the day the money is collected. It decides which mandates and
   * bookings are considered active (they must run on exactly this day) and becomes the Buchungsdatum
   * of every generated row as well as the requested collection date of the SEPA XML. The billing
   * period reported to DATEV is the month this day falls into. Canonically encoded, see
   * `@adornis/datetime/calendarDay.js`.
   */
  @Field(() => Date)
  stichtag: Maybe<Date>;

  /**
   * A preview (`false`) produces the same zip and writes nothing. A lock export locks the group
   * until its bookings are due again, ends the one-time mandates it collected and marks the
   * one-time Selbstzahler bookings as billed; it is refused for a Stichtag the group already billed.
   * Only the single-group export ({@link groupID}) may lock.
   */
  @Field(() => Boolean, { default: v => v ?? false })
  lock: Maybe<boolean>;
}
